import { endOfMonth, endOfQuarter, format, max as maxDate, min as minDate, startOfMonth, startOfQuarter, startOfYear, subMonths, subQuarters } from 'date-fns';

// Period presets for the Actuals vs Budget report.
export const PERIOD_FULL_PROJECT = 'full_project';
export const PERIOD_YEAR_TO_DATE = 'year_to_date';
export const PERIOD_CURRENT_MONTH = 'current_month';
export const PERIOD_LAST_MONTH = 'last_month';
export const PERIOD_LAST_QUARTER = 'last_quarter';
export const PERIOD_CUSTOM = 'custom';

export const DEFAULT_PERIOD_PRESET = PERIOD_FULL_PROJECT;

/**
 * Clamp a month (any day within it) to the inclusive [lo, hi] month window so
 * a preset can never resolve to a month outside the project's own range.
 */
const clampMonth = (date, lo, hi) => maxDate([lo, minDate([startOfMonth(date), hi])]);

/**
 * Pure preset → { startMonth, endMonth } resolver. Both returned values are
 * first-of-month `Date`s, already clamped to the project's month window.
 *
 * Definitions (product-confirmed):
 *   - Full project   — the event's whole range (project min..max month).
 *   - Year to date   — 1 Jan of the current year → the current month.
 *   - Current month  — this month (relative to `today`).
 *   - Last month     — the previous month (relative to `today`).
 *   - Last quarter   — the last COMPLETE calendar quarter.
 *   - Custom         — leaves the caller's current months untouched (clamped).
 *
 * `today` is injected so the today-relative presets stay testable/inspectable.
 *
 * @param {string} preset
 * @param {Object} opts
 * @param {Date|string} opts.projectMin   project start (configs.minDate)
 * @param {Date|string} opts.projectMax   project end   (configs.maxDate)
 * @param {Date}        [opts.today]       defaults to now
 * @param {Date}        [opts.currentStartMonth] current start (used by Custom)
 * @param {Date}        [opts.currentEndMonth]   current end   (used by Custom)
 * @returns {{ startMonth: Date, endMonth: Date }}
 */
export function computePresetRange(preset, { projectMin, projectMax, today = new Date(), currentStartMonth, currentEndMonth } = {}) {
    const lo = startOfMonth(new Date(projectMin));
    const hi = startOfMonth(new Date(projectMax));

    let start;
    let end;

    switch (preset) {
        case PERIOD_YEAR_TO_DATE:
            start = startOfYear(today);
            end = today;
            break;
        case PERIOD_CURRENT_MONTH:
            start = today;
            end = today;
            break;
        case PERIOD_LAST_MONTH:
            start = subMonths(today, 1);
            end = subMonths(today, 1);
            break;
        case PERIOD_LAST_QUARTER: {
            const lastQuarter = subQuarters(startOfQuarter(today), 1);
            start = lastQuarter;
            end = endOfQuarter(lastQuarter);
            break;
        }
        case PERIOD_CUSTOM:
            start = currentStartMonth || lo;
            end = currentEndMonth || hi;
            break;
        case PERIOD_FULL_PROJECT:
        default:
            start = lo;
            end = hi;
            break;
    }

    return {
        startMonth: clampMonth(start, lo, hi),
        endMonth: clampMonth(end, lo, hi),
    };
}

/**
 * Map a resolved month range to the `Y-m-d` dates the backend expects:
 * start → first of its month, end → last of its month.
 */
export function monthRangeToDates(startMonth, endMonth) {
    return {
        start_date: format(startOfMonth(startMonth), 'yyyy-MM-dd'),
        end_date: format(endOfMonth(endMonth), 'yyyy-MM-dd'),
    };
}
